import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookA, Languages, Sparkles, MessageCircle, GraduationCap, SpellCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../context/ChatContext';
import { parseCourseSuggestion } from '../store/courseConfig$';
import { trackEvent } from '../lib/analytics';

const SECONDS_PER_TAB = 10; // 10 seconds per tab
const TOTAL_DURATION = SECONDS_PER_TAB * 4 * 1000; // 40 seconds total

/**
 * Parse welcome content to extract suggestions by type
 */
function parseSuggestions(content) {
  const suggestions = {};

  // Match all mellow:suggestions blocks
  const regex = /```mellow:suggestions\s*\n([\s\S]*?)```/g;
  let match;

  while ((match = regex.exec(content)) !== null) {
    const blockContent = match[1].trim();

    // Check for type: header
    const typeMatch = blockContent.match(/^type:\s*(\w+)\s*\n---\n([\s\S]*)$/);
    if (typeMatch) {
      const type = typeMatch[1].toLowerCase();
      const lines = typeMatch[2]
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);
      suggestions[type] = lines;
    }
  }

  return suggestions;
}

/**
 * Extract welcome message (h3) from content
 * Uses Intl.Segmenter for proper emoji extraction (handles flags, ZWJ sequences, etc.)
 */
function extractWelcomeMessage(content) {
  // Match ### heading
  const h3Match = content.match(/^###\s*(.+)$/m);
  if (h3Match) {
    const text = h3Match[1].trim();

    // Use Intl.Segmenter for proper grapheme segmentation
    // This correctly handles flag emojis (🇷🇺), ZWJ sequences (👨‍👩‍👧), etc.
    const segmenter = new Intl.Segmenter('en', { granularity: 'grapheme' });
    const segments = [...segmenter.segment(text)];

    if (segments.length > 0) {
      const firstGrapheme = segments[0].segment;
      // Check if first grapheme is an emoji (not just a letter/number)
      const isEmoji = /\p{Emoji}/u.test(firstGrapheme) && !/^[0-9#*]$/.test(firstGrapheme);

      if (isEmoji) {
        return {
          emoji: firstGrapheme,
          text: text.slice(firstGrapheme.length).trimStart()
        };
      }
    }

    return { emoji: null, text };
  }
  return null;
}

export default function WelcomeSuggestions({ content, tabMode = 'query' }) {
  const { t } = useTranslation('chat');
  const [progress, setProgress] = useState(0); // 0-100% across all tabs
  const { sendMessage, lookupDictionary, lookupGrammar, lookupNative, lookupCorrection, startCourseWithConfig, sending, markWelcomeUsedAndPrefetch } = useChat();

  // Tab configuration using translations
  const TABS = [
    {
      id: 'ask',
      label: t('input.mode.ask.label'),
      subtitle: t('input.mode.ask.subtitle'),
      icon: MessageCircle,
      color: '#666'
    },
    {
      id: 'course',
      label: t('input.mode.course.label'),
      subtitle: t('input.mode.course.subtitle'),
      icon: GraduationCap,
      color: '#10b981'
    },
    {
      id: 'native',
      label: t('input.mode.native.label'),
      subtitle: t('input.mode.native.subtitle'),
      icon: Sparkles,
      color: '#f5a623'
    },
    {
      id: 'dict',
      label: t('input.mode.dict.label'),
      subtitle: t('input.mode.dict.subtitle'),
      icon: BookA,
      color: '#3498db'
    },
    {
      id: 'grammar',
      label: t('input.mode.grammar.label'),
      subtitle: t('input.mode.grammar.subtitle'),
      icon: Languages,
      color: '#9b59b6'
    },
    {
      id: 'correction',
      label: t('input.mode.correction.label'),
      subtitle: t('input.mode.correction.subtitle'),
      icon: SpellCheck,
      color: '#FF7675'
    },
  ];

  const suggestions = parseSuggestions(content);
  const welcomeMessage = extractWelcomeMessage(content);

  // Filter tabs by tabMode (dict first for query = dictionary positioning)
  const QUERY_TAB_ORDER = ['dict', 'grammar', 'native', 'correction', 'ask'];
  const COURSE_TAB_ORDER = ['course'];
  const tabOrder = tabMode === 'course' ? COURSE_TAB_ORDER : QUERY_TAB_ORDER;

  // Filter to only tabs that match this tab's mode AND have suggestions
  const availableTabs = TABS
    .filter(tab => tabOrder.includes(tab.id) && suggestions[tab.id]?.length > 0)
    .sort((a, b) => tabOrder.indexOf(a.id) - tabOrder.indexOf(b.id));
  const tabCount = availableTabs.length;
  const segmentSize = tabCount > 0 ? 100 / tabCount : 25;

  // Calculate active tab from progress (0-25% = tab 0, 25-50% = tab 1, etc.)
  const activeTab = useMemo(() => {
    if (tabCount === 0) return 0;
    const tabIndex = Math.floor(progress / segmentSize);
    return Math.min(tabIndex, tabCount - 1);
  }, [progress, segmentSize, tabCount]);

  // Auto-rotate logic - progress goes 0-100% continuously
  useEffect(() => {
    if (tabCount <= 1) return;

    const updateInterval = 50; // Update every 50ms
    const incrementPerUpdate = (100 / (TOTAL_DURATION / updateInterval));

    const progressInterval = setInterval(() => {
      setProgress(prev => {
        const next = prev + incrementPerUpdate;
        return next >= 100 ? 0 : next;
      });
    }, updateInterval);

    return () => clearInterval(progressInterval);
  }, [tabCount]);

  // Click tab jumps progress to that tab's start position
  const handleTabClick = useCallback((index) => {
    setProgress(index * segmentSize);
  }, [segmentSize]);

  const handleSuggestionClick = useCallback((suggestion, mode, suggestionIndex, tabId) => {
    if (sending) return;

    // Track welcome suggestion click
    const eventProps = {
      mode,
      suggestion_text: suggestion,
      suggestion_index: suggestionIndex,
      tab_id: tabId,
    };

    // For course mode, add parsed topic and config override info
    if (mode === 'course') {
      const { topic, override } = parseCourseSuggestion(suggestion);
      eventProps.course_topic = topic;
      eventProps.has_config_override = Boolean(override);
      if (override) {
        eventProps.config_override_key = Object.keys(override)[0];
      }
    }

    trackEvent('welcome_suggestion_clicked', eventProps);

    // Mark welcome as used and prefetch new one for next time
    markWelcomeUsedAndPrefetch();

    switch (mode) {
      case 'dict':
        lookupDictionary(suggestion);
        break;
      case 'grammar':
        lookupGrammar(suggestion);
        break;
      case 'native':
        lookupNative(suggestion);
        break;
      case 'correction':
        lookupCorrection(suggestion);
        break;
      case 'course': {
        // Parse course suggestion format: "topic - label | configKey=value"
        const { topic, override } = parseCourseSuggestion(suggestion);
        startCourseWithConfig(topic, override);
        break;
      }
      default:
        sendMessage(suggestion);
    }
  }, [sending, sendMessage, lookupDictionary, lookupGrammar, lookupNative, lookupCorrection, startCourseWithConfig, markWelcomeUsedAndPrefetch]);

  if (availableTabs.length === 0) {
    return null;
  }

  const currentTab = availableTabs[activeTab];
  const currentSuggestions = suggestions[currentTab.id] || [];

  return (
    <div className="welcome-suggestions">
      {/* Welcome Message */}
      {welcomeMessage && (
        <div className="welcome-message">
          {welcomeMessage.emoji && (
            <span className="welcome-emoji">{welcomeMessage.emoji}</span>
          )}
          <h3>{welcomeMessage.text}</h3>
        </div>
      )}

      {/* Tab Bar - hidden when single tab (e.g. course tab) */}
      {tabCount > 1 && (
        <div className="suggestions-tabs">
          {availableTabs.map((tab, index) => {
            const Icon = tab.icon;
            const isActive = index === activeTab;

            return (
              <button
                key={tab.id}
                className={`suggestions-tab ${isActive ? 'active' : ''}`}
                onClick={() => handleTabClick(index)}
                style={{ '--tab-color': tab.color }}
              >
                <Icon size={14} />
                <span className="tab-label">{tab.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Progress Bar - hidden when single tab */}
      {tabCount > 1 && (
        <div className="suggestions-progress">
          <motion.div
            className="suggestions-progress-bar"
            style={{
              width: `${progress}%`,
              backgroundColor: currentTab.color
            }}
          />
        </div>
      )}

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentTab.id}
          className="suggestions-content"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          <p className="suggestions-subtitle">{currentTab.subtitle}</p>
          
          <div className="suggestions-buttons">
            {currentSuggestions.map((suggestion, idx) => {
              // For course mode, parse and show topic + label only
              let displayText = suggestion;
              let label = null;
              if (currentTab.id === 'course') {
                const parsed = parseCourseSuggestion(suggestion);
                displayText = parsed.topic;
                label = parsed.label;
              }

              return (
                <motion.button
                  key={idx}
                  className={`suggestion-btn ${currentTab.id === 'course' ? 'course-suggestion' : ''}`}
                  onClick={() => handleSuggestionClick(suggestion, currentTab.id, idx, currentTab.id)}
                  disabled={sending}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.03 }}
                >
                  <span className="suggestion-text">{displayText}</span>
                  {label && <span className="suggestion-label">{label}</span>}
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

